import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, SlidersHorizontal, X } from "lucide-react";
import { Link } from "react-router-dom";
import { usePrivacyConsent } from "../../contexts/PrivacyConsentContext";
import type { ConsentPreferences } from "../../legal/legal.types";

export function CookieConsentBanner() {
  const {
    hasMadeChoice,
    preferences,
    saveConsent,
    acceptEssentialOnly,
    acceptRecommended,
    acceptAll,
  } = usePrivacyConsent();

  const [showDetails, setShowDetails] = useState(false);
  const [draft, setDraft] = useState<ConsentPreferences>(preferences);

  if (hasMadeChoice) return null;

  const updateDraft = (key: keyof ConsentPreferences, value: boolean) => {
    if (key === "essential") return;

    setDraft((prev) => ({
      ...prev,
      essential: true,
      [key]: value,
    }));
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 36, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 36, scale: 0.98 }}
        transition={{ duration: 0.28 }}
        className="fixed bottom-4 left-4 right-4 z-[300] mx-auto max-w-4xl"
      >
        <div className="overflow-hidden rounded-[28px] border border-white/10 bg-black/80 text-white shadow-[0_30px_100px_rgba(0,0,0,0.65)] backdrop-blur-2xl">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_10%_0%,rgba(245,158,11,0.16),transparent_35%),radial-gradient(circle_at_90%_100%,rgba(244,63,94,0.13),transparent_40%)]" />

          <div className="relative p-5 md:p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-200">
                <ShieldCheck size={20} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black uppercase tracking-[0.35em] text-amber-200/70">
                  Privacy-first experience
                </p>

                <h2 className="mt-2 text-xl md:text-2xl font-light tracking-[-0.04em]">
                  LRNSY uses only the storage needed to keep your experience safe.
                </h2>

                <p className="mt-3 max-w-2xl text-sm leading-7 text-white/58">
                  Essential storage keeps login, private links, drafts, payments, and security
                  working. Optional analytics and marketing help us improve LRNSY, but we do not use
                  private letters for advertising profiling.
                </p>

                <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-white/45">
                  <Link
                    to="/privacy"
                    className="underline decoration-white/20 underline-offset-4 hover:text-white"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/terms"
                    className="underline decoration-white/20 underline-offset-4 hover:text-white"
                  >
                    Terms of Service
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowDetails((prev) => !prev)}
                    className="inline-flex items-center gap-1 underline decoration-white/20 underline-offset-4 hover:text-white"
                  >
                    <SlidersHorizontal size={12} />
                    Manage choices
                  </button>
                </div>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 grid gap-3 md:grid-cols-2">
                        <ConsentToggle
                          title="Essential"
                          description="Required for login, private links, payment continuity, fraud prevention, and secure platform operation."
                          checked
                          disabled
                          onChange={() => null}
                        />
                        <ConsentToggle
                          title="Functional"
                          description="Keeps preferences, drafts, unfinished experiences, and local continuity smoother."
                          checked={draft.functional}
                          onChange={(checked) => updateDraft("functional", checked)}
                        />
                        <ConsentToggle
                          title="Analytics"
                          description="Helps us understand product reliability, drop-offs, performance, and conversion without reading private letters."
                          checked={draft.analytics}
                          onChange={(checked) => updateDraft("analytics", checked)}
                        />
                        <ConsentToggle
                          title="Marketing"
                          description="Allows limited attribution pixels for campaign measurement. Private letter content is never sent for profiling."
                          checked={draft.marketing}
                          onChange={(checked) => updateDraft("marketing", checked)}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={acceptEssentialOnly}
                    className="rounded-2xl border border-white/10 px-5 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/70 transition hover:bg-white/10 hover:text-white"
                  >
                    Essential only
                  </button>

                  <button
                    type="button"
                    onClick={() => (showDetails ? saveConsent(draft) : acceptRecommended())}
                    className="rounded-2xl border border-amber-200/20 bg-white/10 px-5 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-white transition hover:bg-white/15"
                  >
                    {showDetails ? "Save choices" : "Recommended"}
                  </button>

                  <button
                    type="button"
                    onClick={acceptAll}
                    className="rounded-2xl bg-white px-5 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-black transition hover:bg-amber-100"
                  >
                    Accept all
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={acceptEssentialOnly}
                className="rounded-full p-2 text-white/35 transition hover:bg-white/10 hover:text-white"
                aria-label="Close cookie notice with essential storage only"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ConsentToggle({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
      <span>
        <span className="block text-sm font-semibold text-white">{title}</span>
        <span className="mt-1 block text-xs leading-5 text-white/45">{description}</span>
      </span>

      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-5 w-5 accent-amber-300"
      />
    </label>
  );
}
